import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Share2, Check, Linkedin, Instagram, MapPin, Github } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { captureCrmLead } from "../lib/crm";
import { Logo } from "./Logo";
import {
  LEGAL_ENTITY, CORPORATE_ADDRESS, CORPORATE_ADDRESS_2, REGISTERED_ADDRESS,
  PRIMARY_EMAIL, PRIMARY_PHONE, SOCIAL_LINKS
} from "../data";

const LEGAL = [
  { label: "Privacy Policy", to: "/legal/privacy" },
  { label: "Terms & Conditions", to: "/legal/terms" },
  { label: "Refund Policy", to: "/legal/refund" },
  { label: "Cookie Policy", to: "/legal/cookie" },
  { label: "Disclaimer", to: "/legal/disclaimer" },
  { label: "Cancellation Policy", to: "/legal/cancellation" },
  { label: "Sitemap", to: "/sitemap" },
];

const OFFERINGS = [
  { label: "Software Development", to: "/services/software-development" },
  { label: "AI Solutions", to: "/services/ai-solutions" },
  { label: "IoT & Automation", to: "/services/iot-solutions" },
  { label: "Cybersecurity", to: "/guardian" },
  { label: "Cloud Infrastructure", to: "/services/cloud-infrastructure" },
  { label: "Technology Consultancy", to: "/services/consultancy" },
  { label: "nORBIT Enterprise OS", to: "/norbit" },
  { label: "Reach Growth Engine", to: "/reach" },
];

const COMPANY = [
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Our Work", to: "/portfolio" },
  { label: "Blog", to: "/blog" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState({
    corporate_address: CORPORATE_ADDRESS,
    corporate_address_2: CORPORATE_ADDRESS_2,
    registered_address: REGISTERED_ADDRESS,
    legal_entity: LEGAL_ENTITY,
    primary_email: PRIMARY_EMAIL,
    primary_phone: PRIMARY_PHONE,
  });

  useEffect(() => {
    api.get("/settings")
      .then((res) => {
        if (res.data) setSettings((prev) => ({ ...prev, ...res.data }));
      })
      .catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      // Direct CRM webhook capture
      captureCrmLead({
        name: "Newsletter Subscriber",
        email: email.trim().toLowerCase(),
        service: "Newsletter Subscription",
        notes: "Subscribed via website footer newsletter form",
      });

      const { data } = await api.post("/newsletter", { email });
      toast.success(data.status === "already_subscribed" ? "You're already subscribed." : "Subscribed. Welcome aboard!");
      setEmail("");
    } catch { toast.error("Could not subscribe. Try again."); }
    finally { setLoading(false); }
  };

  const handleShare = async () => {
    const shareUrl = "https://neuravolt.in";
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Shareable website link copied to clipboard!");
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.info("Website link: https://neuravolt.in");
    }
  };

  return (
    <footer data-testid="footer" className="section-dark relative pt-16 sm:pt-20 pb-32 sm:pb-36 lg:pb-24 px-5 sm:px-8 overflow-hidden">
      <div className="blob w-[420px] h-[420px] bg-[#FF5A1F] -bottom-40 -left-20 pointer-events-none" style={{ opacity: 0.18 }} />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-10 xl:gap-8">
          {/* Col 1: Logo, Bio, Direct Contacts, Newsletter & Socials */}
          <div className="md:col-span-2 xl:col-span-4 space-y-6">
            <div>
              <div className="mb-4">
                <Logo dark={true} />
              </div>

              <p className="text-neutral-400 max-w-sm leading-relaxed text-xs sm:text-sm">
                AI first software engineering: custom software, AI, mobile, cloud and enterprise solutions for ambitious businesses worldwide.
              </p>
            </div>

            {/* Direct Work Email & Phone Contacts */}
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2.5 text-neutral-300">
                <Mail className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                <span className="text-neutral-400 shrink-0">Work Email:</span>
                <a
                  href={`mailto:${settings.primary_email || PRIMARY_EMAIL}`}
                  className="text-white hover:text-[#FF5A1F] font-medium transition-colors truncate"
                >
                  {settings.primary_email || PRIMARY_EMAIL}
                </a>
              </div>
              <div className="flex items-center gap-2.5 text-neutral-300">
                <Phone className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                <span className="text-neutral-400 shrink-0">Phone / WhatsApp:</span>
                <a
                  href={`tel:${(settings.primary_phone || PRIMARY_PHONE).replace(/\s+/g, "")}`}
                  className="text-white hover:text-[#FF5A1F] font-medium transition-colors whitespace-nowrap"
                >
                  {settings.primary_phone || PRIMARY_PHONE}
                </a>
              </div>
            </div>

            {/* Newsletter Subscription: Bulletproof integrated flex pill */}
            <form onSubmit={subscribe} className="w-full max-w-sm" data-testid="newsletter-form">
              <div className="flex items-center rounded-full bg-white/5 border border-white/10 focus-within:border-[#FF5A1F] p-1 transition-colors w-full">
                <input
                  data-testid="newsletter-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full min-w-0 bg-transparent pl-4 pr-2 py-2 text-xs sm:text-sm text-white outline-none placeholder:text-neutral-500"
                />
                <button
                  data-testid="newsletter-submit"
                  type="submit"
                  disabled={loading}
                  className="shrink-0 px-4 sm:px-5 py-2 rounded-full bg-[#FF5A1F] text-white text-xs sm:text-sm font-semibold hover:bg-[#ff6d38] transition-colors disabled:opacity-60 shadow-md shadow-[#FF5A1F]/30"
                >
                  {loading ? "..." : "Join"}
                </button>
              </div>
            </form>

            {/* Social Links & Share Website */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <a
                href="https://www.linkedin.com/company/neuravolt-technology/posts/?feedView=all"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Neuravolt LinkedIn Company Page"
                title="LinkedIn"
                className="w-8 h-8 rounded-xl bg-white/5 border border-white/10 hover:bg-[#0A66C2] hover:border-[#0A66C2] flex items-center justify-center transition-all duration-200 group shrink-0"
              >
                <Linkedin className="w-3.5 h-3.5 text-neutral-400 group-hover:text-white transition-colors" />
              </a>
              <a
                href={SOCIAL_LINKS.instagram || "https://instagram.com/we.neuravolt"}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Neuravolt Instagram"
                title="Instagram"
                className="w-8 h-8 rounded-xl bg-white/5 border border-white/10 hover:bg-gradient-to-br hover:from-[#833ab4] hover:via-[#fd1d1d] hover:to-[#fcb045] hover:border-transparent flex items-center justify-center transition-all duration-200 group shrink-0"
              >
                <Instagram className="w-3.5 h-3.5 text-neutral-400 group-hover:text-white transition-colors" />
              </a>
              <a
                href={SOCIAL_LINKS.github || "https://github.com/neuravolt-technologies"}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Neuravolt GitHub"
                title="GitHub"
                className="w-8 h-8 rounded-xl bg-white/5 border border-white/10 hover:bg-white/20 hover:border-white/30 flex items-center justify-center transition-all duration-200 group shrink-0"
              >
                <Github className="w-3.5 h-3.5 text-neutral-400 group-hover:text-white transition-colors" />
              </a>
              <a
                href={SOCIAL_LINKS.google_maps || "https://www.google.com/maps/place/Neuravolt+Technology/data=!4m2!3m1!1s0x0:0x47b13dd0dbd4c8f5"}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Neuravolt Google Maps Profile & Reviews"
                title="Google Maps"
                className="w-8 h-8 rounded-xl bg-white/5 border border-white/10 hover:bg-[#FF5A1F] hover:border-[#FF5A1F] flex items-center justify-center transition-all duration-200 group shrink-0"
              >
                <MapPin className="w-3.5 h-3.5 text-neutral-400 group-hover:text-white transition-colors" />
              </a>
              <button
                type="button"
                onClick={handleShare}
                aria-label="Share website link"
                title="Copy shareable website link"
                className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 hover:border-[#FF5A1F] text-xs text-neutral-300 hover:text-white flex items-center gap-1.5 transition-all shrink-0"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5 text-[#FF5A1F]" />}
                <span>{copied ? "Copied!" : "Share"}</span>
              </button>
            </div>
          </div>

          {/* Col 2: Office Addresses */}
          <div className="xl:col-span-4 space-y-3.5">
            <h4 className="font-heading font-semibold text-xs sm:text-sm uppercase tracking-widest text-neutral-400 flex items-center gap-1.5 mb-2">
              <MapPin className="w-3.5 h-3.5 text-[#FF5A1F]" />
              <span>Office Addresses</span>
            </h4>
            
            <div className="space-y-3 text-xs text-neutral-300">
              <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 sm:p-3.5 space-y-1">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-bold text-white block">Corporate Office 1 (HQ)</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#FF5A1F]/15 text-[#FF5A1F] border border-[#FF5A1F]/30 font-semibold">HQ</span>
                </div>
                <p className="text-neutral-400 leading-relaxed text-[11px] sm:text-xs">{settings.corporate_address || CORPORATE_ADDRESS}</p>
              </div>

              <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 sm:p-3.5 space-y-1">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-bold text-white block">Corporate Office 2 (Gurugram Branch)</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-blue-500/15 text-blue-400 border border-blue-500/30 font-semibold">Branch</span>
                </div>
                <p className="text-neutral-400 leading-relaxed text-[11px] sm:text-xs">{settings.corporate_address_2 || CORPORATE_ADDRESS_2}</p>
              </div>

              <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 sm:p-3.5 space-y-1">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-bold text-white block">Registered Office</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-semibold">Legal</span>
                </div>
                <p className="text-neutral-400 leading-relaxed text-[11px] sm:text-xs">{settings.registered_address || REGISTERED_ADDRESS}</p>
              </div>
            </div>
          </div>

          {/* Col 3: Navigation Links */}
          <div className="md:col-span-2 xl:col-span-4 grid grid-cols-3 gap-3 sm:gap-6">
            {/* Services & Products */}
            <div>
              <h4 className="font-heading font-semibold mb-3.5 text-xs sm:text-sm uppercase tracking-widest text-neutral-400">Services & Products</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-neutral-300">
                {OFFERINGS.map((x) => (
                  <li key={x.label}>
                    {x.external ? (
                      <a href={x.to} target="_blank" rel="noopener noreferrer" className="hover:text-[#FF5A1F] transition-colors inline-flex items-center gap-1">
                        <span>{x.label}</span>
                      </a>
                    ) : (
                      <Link to={x.to} className="hover:text-[#FF5A1F] transition-colors">
                        {x.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-heading font-semibold mb-3.5 text-xs sm:text-sm uppercase tracking-widest text-neutral-400">Company</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-neutral-300">
                {COMPANY.map((x) => (
                  <li key={x.label}>
                    <Link to={x.to} className="hover:text-[#FF5A1F] transition-colors">
                      {x.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-heading font-semibold mb-3.5 text-xs sm:text-sm uppercase tracking-widest text-neutral-400">Legal</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-neutral-300">
                {LEGAL.map((x) => (
                  <li key={x.label}>
                    <Link to={x.to} className="hover:text-[#FF5A1F] transition-colors">
                      {x.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar: Copyright & Attribution */}
        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-neutral-500">
          <p className="text-center sm:text-left">© 2026 Neuravolt. All Rights Reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <p>Powered by <span className="text-neutral-300 font-medium">{settings.legal_entity || LEGAL_ENTITY}</span></p>
            <div className="flex items-center gap-3">
              <a href="https://www.linkedin.com/company/neuravolt-technology/posts/?feedView=all" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" title="LinkedIn" className="hover:text-[#0A66C2] transition-colors">
                <Linkedin className="w-3.5 h-3.5" />
              </a>
              <a href={SOCIAL_LINKS.instagram || "https://instagram.com/we.neuravolt"} target="_blank" rel="noopener noreferrer" aria-label="Instagram" title="Instagram" className="hover:text-[#E1306C] transition-colors">
                <Instagram className="w-3.5 h-3.5" />
              </a>
              <a href={SOCIAL_LINKS.github || "https://github.com/neuravolt-technologies"} target="_blank" rel="noopener noreferrer" aria-label="GitHub" title="GitHub" className="hover:text-white transition-colors">
                <Github className="w-3.5 h-3.5" />
              </a>
              <a href={SOCIAL_LINKS.google_maps || "https://www.google.com/maps/place/Neuravolt+Technology/data=!4m2!3m1!1s0x0:0x47b13dd0dbd4c8f5"} target="_blank" rel="noopener noreferrer" aria-label="Google Maps" title="Google Maps" className="hover:text-[#FF5A1F] transition-colors">
                <MapPin className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
